import { useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DSBox from '../../../design-system/DSBox';
import DSModal from '../../../design-system/DSModal';
import DSIconButton from '../../../design-system/DSIconButton';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import { DSTypography } from '../../../design-system/Typography';
import AffiliateLinkReadyView from './AffiliateLinkReadyView';

type AffiliateLinkModalProps = {
  open: boolean;
  onClose: () => void;
  collectionName: string;
  scaledCartImage?: string;
  generating: boolean;
  errored: boolean;
  ready: boolean;
  url: string | null;
  copied: boolean;
  onCopy: () => void;
  onViewPage: () => void;
  onRetry: () => void;
};

const AffiliateLinkModal = ({
  open,
  onClose,
  collectionName,
  scaledCartImage,
  generating,
  errored,
  ready,
  url,
  copied,
  onCopy,
  onViewPage,
  onRetry,
}: AffiliateLinkModalProps) => {
  const { palette, spacing } = useTheme();

  return (
    <DSModal open={open} onClose={onClose}>
      <DSBox
        sx={{
          position: 'relative',
          width: 440,
          maxWidth: '100%',
          p: `${spacing(4.25)} ${spacing(4.25)} ${spacing(3.75)}`,
        }}
      >
        <DSIconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: spacing(2),
            right: spacing(2),
            width: 32,
            height: 32,
            color: palette.white[56],
            backgroundColor: palette.white[5],
            '&:hover': { backgroundColor: palette.white[10] },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </DSIconButton>

        <DSBox sx={{ display: 'flex', justifyContent: 'center' }}>
          <DSBox sx={{ position: 'relative', width: 300, maxWidth: '100%' }}>
            {scaledCartImage ? (
              <img
                src={scaledCartImage}
                alt={`${collectionName} Klydoclock`}
                style={{
                  width: '100%',
                  display: 'block',
                  borderRadius: spacing(2),
                  filter: generating ? 'brightness(0.5) saturate(0.7)' : 'none',
                  transition: 'filter 0.3s ease',
                }}
              />
            ) : (
              <DSBox
                sx={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: spacing(2),
                  backgroundColor: palette.white[5],
                }}
              />
            )}
            {generating && (
              <DSBox
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DSCircularProgress size={54} />
              </DSBox>
            )}
          </DSBox>
        </DSBox>

        {generating && (
          <DSBox sx={{ textAlign: 'center', mt: spacing(3) }}>
            <DSTypography
              variant="h6"
              sx={{
                color: palette.white[90],
                fontFamily: 'Gilroy-Bold',
                lineHeight: 1.3,
              }}
            >
              {`Generating ${collectionName} Klydoclock`}
              <DSBox
                component="span"
                sx={{
                  display: 'inline-block',
                  width: '1.2em',
                  textAlign: 'left',
                  overflow: 'hidden',
                  verticalAlign: 'bottom',
                  animation: 'affiliateDots 1.4s steps(4, end) infinite',
                  '@keyframes affiliateDots': {
                    '0%': { width: 0 },
                    '100%': { width: '1.2em' },
                  },
                  '&:after': { content: '"…"' },
                }}
              />
            </DSTypography>
            <DSTypography
              variant="body2"
              sx={{
                color: palette.white[70],
                mt: spacing(1.25),
                maxWidth: 320,
                mx: 'auto',
                lineHeight: 1.55,
              }}
            >
              {`Building your branded ${collectionName} page and unique bundle klydoclock link. This only takes a moment.`}
            </DSTypography>
          </DSBox>
        )}

        {errored && (
          <DSBox sx={{ textAlign: 'center', mt: spacing(3) }}>
            <DSTypography variant="h6" sx={{ color: palette.white[90] }}>
              Something went wrong
            </DSTypography>
            <DSTypography
              variant="body2"
              sx={{ color: palette.white[70], mt: spacing(1.25) }}
            >
              We couldn’t generate your Bundle link. Please try again.
            </DSTypography>
            <DSPrimaryButton onClick={onRetry} sx={{ mt: spacing(2.5) }}>
              Try again
            </DSPrimaryButton>
          </DSBox>
        )}

        {ready && (
          <AffiliateLinkReadyView
            collectionName={collectionName}
            url={url}
            copied={copied}
            onCopy={onCopy}
            onViewPage={onViewPage}
          />
        )}
      </DSBox>
    </DSModal>
  );
};

export default AffiliateLinkModal;
